import { ScrollableTabsList } from '@workspace/ui/components/scrollable-tabs'
import { Tabs, TabsContent, TabsTrigger } from '@workspace/ui/components/tabs'
import { MarkdownContent } from '@/components/markdown-content'
import type { CollectiveMetricEvaluation } from '../types/pitch.types'
import { ScoreBadge } from './score-badge'

interface MetricsTabsProps {
  metrics: CollectiveMetricEvaluation[]
}

const METRIC_ORDER = ['team', 'traction', 'market', 'problem_solution', 'vision']

const METRIC_LABELS: Record<string, string> = {
  market: 'Market',
  problem_solution: 'Problem/Solution',
  team: 'Team',
  traction: 'Traction',
  vision: 'Vision',
}

export function MetricsTabs({ metrics }: MetricsTabsProps) {
  // Sort metrics by defined order
  const sortedMetrics = [...metrics].sort((a, b) => {
    const aIndex = METRIC_ORDER.indexOf(a.metric)
    const bIndex = METRIC_ORDER.indexOf(b.metric)
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex)
  })

  const firstMetric = sortedMetrics[0]
  if (!firstMetric) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-center text-muted-foreground">
        No metric evaluations available yet.
      </div>
    )
  }

  return (
    <Tabs defaultValue={firstMetric.metric} className="w-full">
      <ScrollableTabsList gradientFrom="card">
        {sortedMetrics.map((metric) => (
          <TabsTrigger key={metric.id} value={metric.metric} className="gap-2">
            <span>{METRIC_LABELS[metric.metric] ?? metric.metric}</span>
            {metric.score !== null && <ScoreBadge score={metric.score} size="sm" />}
          </TabsTrigger>
        ))}
      </ScrollableTabsList>

      {sortedMetrics.map((metric) => (
        <TabsContent key={metric.id} value={metric.metric} className="mt-4">
          {metric.summary ? (
            <MarkdownContent>{metric.summary}</MarkdownContent>
          ) : (
            <p className="text-muted-foreground text-center">
              No summary available for this metric.
            </p>
          )}
        </TabsContent>
      ))}
    </Tabs>
  )
}
